import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import ImmersiveLayout from '../components/ImmersiveLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, CreditCard, Copy, MapPin, Calendar, Loader2, MessageCircle, Package, XCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const STATUS_TONE = {
  pending_payment: { bg: 'bg-amber-400/10', text: 'text-amber-300', label_id: 'Menunggu Pembayaran', label_en: 'Pending Payment' },
  paid: { bg: 'bg-seikat-lime/15', text: 'text-seikat-lime', label_id: 'Dibayar', label_en: 'Paid' },
  accepted: { bg: 'bg-blue-400/10', text: 'text-blue-300', label_id: 'Diterima', label_en: 'Accepted' },
  in_progress: { bg: 'bg-seikat-pink/15', text: 'text-seikat-pink', label_id: 'Dirangkai', label_en: 'Crafting' },
  packed: { bg: 'bg-seikat-pink/15', text: 'text-seikat-pink', label_id: 'Dikemas', label_en: 'Packed' },
  out_for_delivery: { bg: 'bg-seikat-lime/15', text: 'text-seikat-lime', label_id: 'Dikirim', label_en: 'Out for Delivery' },
  delivered: { bg: 'bg-seikat-lime/15', text: 'text-seikat-lime', label_id: 'Terkirim', label_en: 'Delivered' },
  completed: { bg: 'bg-seikat-pink/15', text: 'text-seikat-pink', label_id: 'Selesai', label_en: 'Completed' },
  cancelled: { bg: 'bg-red-500/10', text: 'text-red-300', label_id: 'Dibatalkan', label_en: 'Cancelled' }
};

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [bankConfig, setBankConfig] = useState(null);
  const [whatsappLink, setWhatsappLink] = useState('');
  const [loading, setLoading] = useState(true);
  const id = language === 'id';

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    Promise.all([
      axios.get(`${API}/orders/${orderId}`, { withCredentials: true }).then(r => r.data),
      axios.get(`${API}/settings/bank-transfer`).then(r => r.data).catch(() => null),
      axios.get(`${API}/orders/${orderId}/whatsapp`, { withCredentials: true }).then(r => r.data?.whatsapp_link).catch(() => '')
    ]).then(([o, b, wa]) => {
      setOrder(o);
      setBankConfig(b);
      setWhatsappLink(wa);
    }).catch(() => {
      toast.error(id ? 'Tidak dapat memuat pesanan' : 'Could not load order');
      navigate('/orders');
    }).finally(() => setLoading(false));
  }, [orderId, user, navigate, id]);

  const fmt = (p) => new Intl.NumberFormat('id-ID').format(p);
  const fmtDate = (d) => new Date(d).toLocaleString(id ? 'id-ID' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const handleCancel = async () => {
    if (!window.confirm(id ? 'Batalkan pesanan ini?' : 'Cancel this order?')) return;
    try {
      const res = await axios.post(`${API}/orders/${orderId}/cancel`, {}, { withCredentials: true });
      setOrder(res.data);
      toast.success(id ? 'Pesanan dibatalkan.' : 'Order cancelled.');
    } catch (err) {
      toast.error(err.response?.data?.detail || (id ? 'Gagal membatalkan' : 'Failed to cancel'));
    }
  };

  if (loading) {
    return (
      <ImmersiveLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 size={24} className="animate-spin text-seikat-pink" />
        </div>
      </ImmersiveLayout>
    );
  }

  if (!order) return null;

  const tone = STATUS_TONE[order.status] || STATUS_TONE.pending_payment;

  return (
    <ImmersiveLayout>
      <section className="px-6 lg:px-16 pt-24 pb-32" data-testid="order-detail-page">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate('/orders')} className="flex items-center gap-2 text-xs tracking-widest uppercase text-seikat-bone-muted hover:text-seikat-pink transition-colors mb-10" data-testid="back-orders">
            <ArrowLeft size={14} /> {id ? 'Kembali ke Pesanan' : 'Back to Orders'}
          </button>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-seikat-pink" />
              <span className="eyebrow-immersive">Order Detail</span>
            </div>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
              <h1 className="font-heading text-5xl text-seikat-bone leading-[0.95]">
                {id ? <>Rangkaian <em>{order.order_id.slice(-4)}</em></> : <>Weave <em>{order.order_id.slice(-4)}</em></>}
              </h1>
              <span className={`px-3 py-1.5 rounded-full text-[10px] tracking-widest uppercase ${tone.bg} ${tone.text}`} data-testid="order-status-badge">
                {id ? tone.label_id : tone.label_en}
              </span>
            </div>
            <p className="mt-4 font-body text-seikat-bone-muted text-sm">
              <code className="font-mono text-seikat-bone">{order.order_id}</code> · {fmtDate(order.created_at)}
            </p>
            {/* Cancel button for pending orders */}
            {(order.status === 'pending_payment' || order.status === 'paid') && (
              <button onClick={handleCancel} className="mt-4 flex items-center gap-2 text-xs text-red-400 hover:text-red-300 transition-colors font-body tracking-wide" data-testid="cancel-order-btn">
                <XCircle size={13} /> {id ? 'Batalkan Pesanan' : 'Cancel Order'}
              </button>
            )}
          </motion.div>

          <div className="grid lg:grid-cols-[1fr_360px] gap-8 mt-14">
            {/* Left: items + delivery */}
            <div className="space-y-6">
              <div className="glass-dark rounded-2xl p-7">
                <header className="flex items-center gap-3 mb-6">
                  <Package size={16} className="text-seikat-lime" />
                  <h2 className="font-heading text-2xl text-seikat-bone">{id ? 'Rangkaian dalam Pesanan' : 'Items in Order'}</h2>
                </header>
                <div className="space-y-4">
                  {(order.items || []).map((item, i) => (
                    <div key={i} className="flex gap-4 border-b border-white/8 last:border-0 pb-4 last:pb-0">
                      {item.image && (
                        <img src={item.image} alt={item.name_en || item.name} className="w-20 h-24 object-cover rounded-lg flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <h4 className="font-heading text-lg text-seikat-bone">{id ? (item.name_id || item.name) : (item.name_en || item.name)}</h4>
                        <p className="font-body text-xs text-seikat-bone-muted mt-1">Qty {item.quantity || 1}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-body text-sm text-seikat-pink">Rp {fmt((item.price || 0) * (item.quantity || 1))}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-dark rounded-2xl p-7">
                <header className="flex items-center gap-3 mb-5">
                  <MapPin size={16} className="text-seikat-pink" />
                  <h2 className="font-heading text-2xl text-seikat-bone">{id ? 'Pengiriman' : 'Delivery'}</h2>
                </header>
                <div className="space-y-2 font-body text-sm">
                  <Row label={id ? 'Nama' : 'Name'} value={order.delivery_name || '—'} />
                  <Row label={id ? 'Telepon' : 'Phone'} value={order.delivery_phone || '—'} />
                  <Row label={id ? 'Alamat' : 'Address'} value={order.delivery_address || '—'} />
                  {order.delivery_date && <Row label={id ? 'Tanggal' : 'Date'} value={order.delivery_date} />}
                  {order.delivery_notes && <Row label={id ? 'Catatan' : 'Notes'} value={order.delivery_notes} />}
                </div>
              </div>
            </div>

            {/* Right: summary + payment */}
            <aside className="lg:sticky lg:top-10 h-fit space-y-6">
              <div className="glass-dark rounded-2xl p-7">
                <p className="eyebrow-immersive text-seikat-lime">{id ? 'Total' : 'Total'}</p>
                <h3 className="font-heading text-4xl text-seikat-bone mt-1">Rp {fmt(order.total)}</h3>
                <div className="mt-5 space-y-2 font-body text-sm">
                  <Row label={id ? 'Subtotal' : 'Subtotal'} value={`Rp ${fmt(order.subtotal || 0)}`} />
                  {order.delivery_fee && <Row label={id ? 'Ongkir' : 'Delivery'} value={`Rp ${fmt(order.delivery_fee)}`} />}
                  {order.discount && <Row label={id ? 'Diskon' : 'Discount'} value={`-Rp ${fmt(order.discount)}`} accent="lime" />}
                </div>
              </div>

              {bankConfig && order.status === 'pending_payment' && (
                <div className="glass-dark rounded-2xl p-7">
                  <header className="flex items-center gap-3 mb-5">
                    <CreditCard size={16} className="text-seikat-pink" />
                    <h2 className="font-heading text-xl text-seikat-bone">{id ? 'Transfer Bank' : 'Bank Transfer'}</h2>
                  </header>
                  <div className="space-y-2 font-body text-sm">
                    <Row label="Bank" value={bankConfig.bank_name} />
                    <Row
                      label={id ? 'No. Rek' : 'Account'}
                      value={
                        <button onClick={() => { navigator.clipboard.writeText(bankConfig.account_number); toast.success('Copied'); }} className="inline-flex items-center gap-1 text-seikat-bone hover:text-seikat-pink transition-colors">
                          {bankConfig.account_number} <Copy size={12} />
                        </button>
                      }
                    />
                    <Row label={id ? 'a.n.' : 'Name'} value={bankConfig.account_name} />
                  </div>
                </div>
              )}

              {whatsappLink && (
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="block">
                  <button className="btn-pill w-full justify-center" style={{ background: '#25D366', color: '#FFF', borderColor: '#25D366' }} data-testid="order-whatsapp">
                    <MessageCircle size={14} /> {id ? 'Konfirmasi via WhatsApp' : 'Confirm on WhatsApp'}
                  </button>
                </a>
              )}

              {/* Memory Page addon — link to builder */}
              {order.addons?.includes?.('memory_page') && (
                <Link to="/memories" className="block">
                  <button className="btn-pill btn-pill--solid w-full justify-center" data-testid="order-memory-page-link">
                    <Sparkles size={14} /> {id ? 'Buat Memory Page' : 'Create Memory Page'}
                  </button>
                </Link>
              )}
            </aside>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-seikat-bone-muted whitespace-nowrap">{label}</span>
      <span className={`text-right ${accent === 'lime' ? 'text-seikat-lime' : 'text-seikat-bone'}`}>{value}</span>
    </div>
  );
}
